import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { type Destino, useDestinos } from '../api/destinos'
import { pedir } from '../api/nucleo'
import type { Permisos } from '../api/capacidades'
import { Aviso, Boton, Selector, Tarjeta, claseDeEntrada } from '../componentes/ui'
import { Editor } from '../motor/Editor'

const GRUPOS: Array<{ tipo: Destino['tipo']; etiqueta: string }> = [
  { tipo: 'pagina', etiqueta: 'Páginas' },
  { tipo: 'servicio', etiqueta: 'Servicios' },
  { tipo: 'producto', etiqueta: 'Productos' },
  { tipo: 'entrada', etiqueta: 'Entradas del blog' },
]
const OTRA = '__otra__'

/**
 * Una dirección no encontrada (CLAUDE.md §10): cuántas veces, desde dónde, y el botón
 * que convierte el error en una redirección. El destino se **elige** entre lo publicado
 * de la web (una ruta escrita a mano con una letra de más es otro 404), o se escribe si
 * es otra. El núcleo crea la regla, marca el 404 como resuelto y lo audita, todo junto:
 * lo hace quien edita el SEO de los documentos (`seo.editar`); borrar el 404 o editarlo,
 * quien lleva el SEO de toda la web (`seo.sitio`).
 */
export const FichaDe404 = ({ espacio, sitio, base, permisos }: { base: string; espacio: number; permisos: Permisos; sitio: number }) => {
  const { id } = useParams()
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const error = Number(id)
  const { data: destinos } = useDestinos(sitio)
  const [elegido, setElegido] = useState('')
  const [otra, setOtra] = useState('')
  const [mensaje, setMensaje] = useState<null | string>(null)
  const [ocupado, setOcupado] = useState(false)
  const puede = permisos.tiene('seo.editar')
  const llevaElSeoDeLaWeb = permisos.tiene('seo.sitio')
  const destino = elegido === OTRA ? otra.trim() : elegido

  return (
    <Editor
      base={base}
      cabecera={({ valores }) => (
        <Tarjeta>
          <p className="mb-1 text-xs font-medium tracking-wide text-tenue uppercase">Convertir en redirección</p>
          <p className="mb-3 text-sm text-tenue">
            <code className="rounded bg-papel-2 px-1">{String(valores.ruta ?? '')}</code> se ha pedido {String(valores.veces ?? 0)} veces
            {typeof valores.referente === 'string' && valores.referente ? `, la última desde ${valores.referente}` : ''}.{' '}
            {puede ? 'Elige a dónde debe llevar y se crea la redirección permanente.' : 'Convertirla en redirección lo hace quien edita el SEO de la web.'}
          </p>
          {puede && (
            <form
              className="flex flex-col gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                setOcupado(true)
                setMensaje(null)
                pedir<{ ok: boolean; desde: string; hacia: string }>('/seo/redireccion-desde-404', { metodo: 'POST', cuerpo: { sitio, error, destino } })
                  .then(() => {
                    void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', 'errores-404'] })
                    void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', 'redirecciones'] })
                    navegar(base.replace(/\/404$/, '/redirecciones'))
                  })
                  .catch((err: Error) => setMensaje(err.message))
                  .finally(() => setOcupado(false))
              }}
            >
              <div className="flex flex-wrap gap-2">
                <label className="sr-only" htmlFor="destino-404">
                  A dónde lleva
                </label>
                <span className="min-w-64 flex-1">
                  <Selector id="destino-404" onChange={(e) => setElegido(e.target.value)} value={elegido}>
                    <option value="">Elige una página de tu web…</option>
                    {GRUPOS.map((g) => {
                      const suyos = (destinos ?? []).filter((d) => d.tipo === g.tipo)
                      return suyos.length ? (
                        <optgroup key={g.tipo} label={g.etiqueta}>
                          {suyos.map((d) => (
                            <option key={d.ruta} value={d.ruta}>
                              {d.titulo} — {d.ruta}
                            </option>
                          ))}
                        </optgroup>
                      ) : null
                    })}
                    <option value={OTRA}>Otra dirección de mi web…</option>
                  </Selector>
                </span>
                <Boton disabled={ocupado || !destino} cargando={ocupado} tipo="primario" type="submit">
                  Crear redirección
                </Boton>
              </div>
              {elegido === OTRA && (
                <input
                  aria-label="Otra dirección de tu web"
                  autoCapitalize="none"
                  className={claseDeEntrada()}
                  onChange={(e) => setOtra(e.target.value)}
                  placeholder="/la-direccion-nueva"
                  required
                  spellCheck={false}
                  value={otra}
                />
              )}
              {destinos && destinos.length === 0 && <p className="text-xs text-tenue">Todavía no hay nada publicado en esta web: publica la página de destino primero, o escribe su dirección.</p>}
            </form>
          )}
          {mensaje && (
            <div className="mt-3">
              <Aviso tono="error">{mensaje}</Aviso>
            </div>
          )}
        </Tarjeta>
      )}
      coleccion="errores-404"
      espacio={espacio}
      id={error}
      puedeBorrar={llevaElSeoDeLaWeb}
      sitio={sitio}
      soloLectura={!llevaElSeoDeLaWeb}
    />
  )
}
